import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout } from "../components/Portal";
import { Btn, Spinner, Th, cellClip, formatWhen, useSortableColumns, useColumnWidths } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";

const ACTION_LABELS = {
  // A request that failed. The reference in the detail is the one the caller
  // was shown, so a screenshot of an error is enough to find the entry — which
  // is the whole reason these are written at all.
  "settings.update": { label: "Setting changed",       color: "#b45309" },
  "error.unhandled": { label: "Request failed",        color: "#dc2626" },
  "error.database":  { label: "Database error",        color: "#dc2626" },
  "project.create":  { label: "Project created",       color: "#16a34a" },
  "project.update":  { label: "Project updated",       color: "#2563eb" },
  "project.status":  { label: "Status changed",        color: "#7c3aed" },
  // Nothing has written this since 2026-09-14 — a project's deadlines now
  // come from its group and the route that set them is gone. The label stays
  // because the log still holds the entries written before that, and an
  // unlabelled action renders as its raw string. Removing it reddens
  // backend/tests/test_health_kind_contract.py, which reads this file.
  "project.deadlines": { label: "Deadlines set",        color: "#7c3aed" },
  "project.record_sent": { label: "Record sent to billing", color: "#0891b2" },
  "project.reopen_request": { label: "Reopen requested", color: "#b45309" },
  "project.reopen_approve": { label: "Reopen approved",  color: "#15803d" },
  "project.reopen_reject":  { label: "Reopen refused",   color: "#dc2626" },
  "project.delete":  { label: "Project deleted",       color: "#dc2626" },
  "submission.create": { label: "Submission uploaded", color: "#0891b2" },
  // Written when the button is pressed, not when the archive exists — a run
  // takes hours, so "Results packaged" was asserting a finished thing about one
  // that had just started. The completion is `result.ready` below.
  "result.create":   { label: "Packaging started",     color: "#0891b2" },
  "result.ready":    { label: "Delivery packaged",     color: "#15803d" },
  "result.notify":   { label: "Notification sent",     color: "#d97706" },
  "user.create":     { label: "User created",          color: "#16a34a" },
  "user.update":     { label: "User updated",          color: "#2563eb" },
  "user.deactivate": { label: "User deactivated",      color: "#dc2626" },
  "user.activate":   { label: "User reactivated",      color: "#16a34a" },
  "user.delete":     { label: "User deleted",          color: "#dc2626" },
  // One entry per press of Sync with PPMS, carrying the counts; the projects it
  // created are logged individually as project.create, exactly as a hand-made
  // one is. Written at warning level when PPMS named project ids it could not
  // describe, because then the sync is knowingly incomplete.
  "ppms.sync":       { label: "PPMS sync",             color: "#2563eb" },
  // The copy of PPMS's whole project list the import dialog searches; one
  // entry per refresh that wrote, "585 projects, 3 new, 1 gone" in the detail.
  "ppms.catalog_refresh": { label: "PPMS catalogue refreshed", color: "#2563eb" },
  // One project imported from that catalogue, beside its `project.create`.
  "ppms.import":     { label: "PPMS project imported", color: "#2563eb" },
  "automation.run":    { label: "Automation ran",       color: "#15803d" },
  "automation.failed": { label: "Automation failed",   color: "#dc2626" },
  "automation.test_send": { label: "Test summary sent", color: "#6b7280" },
  "newsletter.schedule_update": { label: "Summary schedule changed", color: "#2563eb" },
  "project.finish":  { label: "Finished",              color: "#059669" },
  "project.cancel":  { label: "Cancelled",             color: "#dc2626" },
  "project.expire":  { label: "Expired",               color: "#d97706" },
  "group.storage_capacity": { label: "Storage capacity set", color: "#6b7280" },
  "group.s3_config": { label: "Group storage changed", color: "#7c3aed" },
  "delivery.upload": { label: "Delivery uploaded",   color: "#0ea5e9" },
  "group.retention": { label: "Retention policy",    color: "#b91c1c" },
  "group.affiliation_vat": { label: "VAT rate",     color: "#b91c1c" },
  "stats.export":    { label: "Statistics export",   color: "#6b7280" },
  "backup.download": { label: "Database backup",     color: "#b91c1c" },
  "backup.import":   { label: "Backup imported",    color: "#b91c1c" },
  "backup.download_group": { label: "Group backup",   color: "#b91c1c" },
  "project.transfer": { label: "Ownership transferred", color: "#7c3aed" },
  "project.member_add":    { label: "User assigned",   color: "#2563eb" },
  "project.member_remove": { label: "User unassigned", color: "#b45309" },
  "submission.notify":   { label: "Notification sent",   color: "#d97706" },
  "storage.unreachable": { label: "Storage unreachable", color: "#b45309" },
  // The bucket answers; what it no longer holds is the data. Calling that
  // "unreachable" sent people to check the endpoint and the credentials.
  "storage.data_gone":   { label: "Storage folder empty", color: "#b45309" },
  "storage.restored":    { label: "Storage restored",    color: "#15803d" },
  "storage.data_missing":  { label: "Data missing",     color: "#b45309" },
  "storage.unexpected_data": { label: "Unexpected data", color: "#b45309" },
  "storage.data_restored": { label: "Data found again", color: "#15803d" },
  "storage.nearly_full": { label: "Storage nearly full", color: "#b45309" },
  // The record, not the data — the entry's own text says so, and this label
  // must not suggest otherwise either.
  "submission.delete": { label: "Submission record removed", color: "#dc2626" },
  "result.delete":     { label: "Delivery record removed",   color: "#dc2626" },
  // These five were written by the backend but had no entry here, so the log
  // showed the raw action string as its own chip.
  "project.s3_config":  { label: "Storage configured",   color: "#2563eb" },
  "project.delivery_prefix": { label: "Delivery subfolder changed", color: "#2563eb" },
  "user.password_reset": { label: "Password reset",      color: "#b45309" },
  "user.welcome_email": { label: "Welcome email sent",   color: "#d97706" },
  "user.profile_submit": { label: "Profile submitted",   color: "#2563eb" },
  "user.confirm":        { label: "Profile confirmed",   color: "#16a34a" },
  "job.cancel":         { label: "Job cancelled",        color: "#b45309" },
  // A failed upload used to appear only on the Jobs page; the log had a label
  // for a cancel but nothing at all for a failure.
  "job.fail":           { label: "Job failed",           color: "#b91c1c" },
  // Deliberately not "Job failed": every byte reached storage and only the
  // record is missing, so the wording must not send anyone looking for lost
  // data or a network fault. See JOB-C58EB8 in CLAUDE.md.
  "job.incomplete":     { label: "Upload not recorded",  color: "#b45309" },
  "submission.recover": { label: "Submission recorded afterwards", color: "#15803d" },
  // An unfinished upload nobody came back for. The parts are gone; no completed
  // object ever was — the wording has to keep those apart.
  "job.upload_expired": { label: "Unfinished upload discarded", color: "#b45309" },
  // Clearing the job history is itself history — this entry is the only record
  // left that those jobs ever existed.
  "job.start":          { label: "Job started",          color: "#0891b2" },
  "job.complete":       { label: "Job finished",         color: "#15803d" },
  "job.clear":          { label: "Job history cleared",  color: "#7c3aed" },
  // Written after the wipe, so it is the first entry of the new log. Without
  // it, clearing the audit trail is the one action the audit trail misses.
  "log.clear":          { label: "Audit log cleared",    color: "#b91c1c" },
  // Kept above for entries written before the log became non-erasable. Nothing
  // produces it any more — clearing is refused, and the refusal is recorded.
  "log.clear_refused":  { label: "Audit log clear refused", color: "#b91c1c" },
  "log.export":         { label: "Log exported",         color: "#6b7280" },

  // Sign-in and sign-out. Absent entirely until the audit work: a trail with no
  // authentication events cannot answer "who was in the system", which is the
  // first question anybody asks of one.
  "auth.login":         { label: "Signed in",            color: "#0891b2" },
  "auth.login_failed":  { label: "Sign-in failed",       color: "#d97706" },
  "auth.locked_out":    { label: "Account locked out",   color: "#dc2626" },
  "auth.logout":        { label: "Signed out",           color: "#6b7280" },

  // Who reached what. project.view is coalesced per sign-in session, so a run
  // of them is not a run of page loads.
  "project.view":       { label: "Project opened",       color: "#6b7280" },
  "delivery.download":  { label: "Delivery downloaded",  color: "#0891b2" },

  "role.grant":         { label: "Role granted",         color: "#7c3aed" },
  "role.revoke":        { label: "Role revoked",         color: "#b45309" },

  // The trail talking about itself.

  // Group management and pricing.
  // A group's two ends. The deletion is red because it is: it takes the
  // group's affiliations, price lists and signed documents with it, and the
  // entry is the only surviving record of them.
  "group.create":       { label: "Group created",        color: "#2563eb" },
  "group.delete":       { label: "Group deleted",        color: "#b91c1c" },
  "group.profile_update": { label: "Group details changed", color: "#2563eb" },
  "price.import":       { label: "Price list imported",  color: "#0891b2" },
  "price.affiliations": { label: "Affiliations added",   color: "#2563eb" },
  "price.approve":      { label: "Price list approved",  color: "#16a34a" },
  "price.publish":      { label: "Price list published", color: "#16a34a" },
  "service.create":     { label: "Services added",       color: "#2563eb" },
  "tag.create":         { label: "Tag created",          color: "#2563eb" },
  "tag.delete":         { label: "Tag removed",          color: "#b45309" },
  "project_field.create": { label: "Project field added",   color: "#2563eb" },
  "project_field.update": { label: "Project field changed", color: "#2563eb" },
  "project_field.delete": { label: "Project field removed", color: "#b45309" },
  "log.prune":          { label: "Group log pruned",     color: "#b91c1c" },
  "project.tags":       { label: "Tags changed",         color: "#2563eb" },
  "project.fields":     { label: "Project details changed", color: "#2563eb" },
  "announcement.send":  { label: "Announcement sent",    color: "#7c3aed" },

  // Quotations.
  "quote.create":       { label: "Quotation created",    color: "#2563eb" },
  "quote.discount":     { label: "Discount changed",     color: "#b45309" },
  "quote.send":         { label: "Quotation sent",       color: "#0891b2" },
  "quote.view":         { label: "Quotation opened",     color: "#6b7280" },
  "quote.accept":       { label: "Quotation accepted",   color: "#16a34a" },
  "quote.reject":       { label: "Quotation declined",   color: "#b45309" },
  "quote.download":     { label: "Quotation downloaded", color: "#6b7280" },
  "quote.withdraw":     { label: "Quotation withdrawn",  color: "#b45309" },
  // The whole facility's offers, in every state, taken off the portal.
  "quote.export":       { label: "Quotations exported", color: "#6b7280" },
  "order.start":        { label: "Order agreed",         color: "#2563eb" },
  // One order ending. All three endings share it — completed, cancelled,
  // expired — because the detail says which, and three labels would put the
  // word in two places to disagree from.
  "order.completion_proposed": { label: "Finish proposed",  color: "#0284c7" },
  "order.close":        { label: "Order closed",         color: "#0891b2" },
  "order.reopen":       { label: "Order reopened",       color: "#d97706" },
  // The project deadline: a project nobody is using, archived after its time.
  "project.overdue":    { label: "Project overdue",      color: "#b45309" },
  "order.overdue":      { label: "Order overdue",        color: "#b45309" },

  // Documents a facility publishes.
  "document.create":      { label: "Document created",     color: "#2563eb" },
  "document.update":      { label: "Document changed",     color: "#2563eb" },
  "document.publish":     { label: "Document published",   color: "#16a34a" },
  "document.change_request": { label: "Policy change proposed", color: "#b45309" },
  "document.change_approve": { label: "Policy change approved", color: "#15803d" },
  "document.change_reject":  { label: "Policy change refused",  color: "#dc2626" },
  "document.change_direct":  { label: "Policy changed without approval", color: "#b45309" },
  "document.acknowledge": { label: "Document acknowledged", color: "#16a34a" },
  "document.download":    { label: "Document downloaded",  color: "#6b7280" },
  // Not the lifecycle. This is the project-archive DOWNLOAD, and the name was
  // taken first — which is why withdrawing a policy is `document.withdraw`.
  "document.archive":  { label: "Documents downloaded", color: "#6b7280" },
  // The lifecycle: withdrawn from force, back in force, and removed. Amber
  // rather than red for a withdrawal — it is deliberate and reversible — and
  // red for a delete, which is only ever possible on a document nobody signed.
  "document.withdraw":   { label: "Document withdrawn",   color: "#d97706" },
  "document.reactivate": { label: "Document back in force", color: "#16a34a" },
  "document.delete":     { label: "Document deleted",     color: "#dc2626" },
  // Two-person approval. `document.publish` now means "submitted"; these two
  // are the acts that put a version into force or refuse it.
  "document.approve":    { label: "Version approved",     color: "#16a34a" },
  "document.reject":     { label: "Version rejected",     color: "#d97706" },
  // The author taking back their own pending version or change — nobody else
  // said no, so neither "rejected" nor "refused".
  "document.retract":    { label: "Version taken back by its author", color: "#6b7280" },
  "document.change_retract": { label: "Policy change taken back", color: "#6b7280" },
  // Other people's signatures leaving the server. Red, like every other
  // "taking data off the server" entry.
  "document.export":     { label: "Signatures exported",  color: "#dc2626" },
  // A group admin reminding the people a policy still waits for.
  "document.notify":     { label: "Confirmation reminder sent", color: "#2563eb" },
  // Asking the admins who may approve a pending version or change.
  "document.approval_request": { label: "Approval requested", color: "#2563eb" },
  // A device switching push notifications on or off for its own account.
  "push.subscribe":      { label: "Push notifications on",  color: "#6b7280" },
  "push.unsubscribe":    { label: "Push notifications off", color: "#6b7280" },
  "submission.download": { label: "Submission downloaded", color: "#6b7280" },
  "ppms.decline":      { label: "PPMS import declined", color: "#6b7280" },

  "audit.write_failed": { label: "Log entry lost",       color: "#dc2626" },
  "settings.notification_update": { label: "Notification settings updated", color: "#2563eb" },
};

function ActionBadge({ action }) {
  const a = ACTION_LABELS[action] ?? { label: action, color: "#6b7280" };
  return (
    <span style={{
      display: "inline-block", fontSize: 11, fontWeight: 600,
      padding: "2px 8px", borderRadius: 8,
      background: a.color + "18", color: a.color,
      whiteSpace: "nowrap",
    }}>
      {a.label}
    </span>
  );
}

// The `kind` values /api/health/problems can return. "storage" and
// "storage-empty" are both s3_health_ok === false, and reporting them in the
// same words is what let a bucket that answers fine be called unreachable.
const PROBLEM_LABELS = {
  "storage":        "storage unreachable",
  "storage-empty":  "storage folder empty — recorded data is not there",
  "data-missing":   "data missing from storage",
  // The opposite of the two above: the data is there, the record is not.
  "upload-unrecorded": "an upload reached storage but was never recorded — open Jobs to record it",
};

const LOG_ACCESSORS = {
  time:     l => new Date(l.created_at).getTime(),
  action:   l => ACTION_LABELS[l.action]?.label || l.action,
  resource: l => l.resource || "",
  actor:    l => l.actor || "",
  detail:   l => l.detail || "",
};

export default function LogPage() {
  const zone = useDisplayTimezone();
  const { user }          = useAuth();
  const [logs, setLogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { sorted, sortKey, sortDir, toggleSort } = useSortableColumns(logs, LOG_ACCESSORS);
  // Deliberately not "how many warnings are in this table". A warning from
  // last week that was fixed an hour later is history, and an indicator that
  // counts it forever stops meaning anything.
  const [problems, setProblems] = useState(null);
  useEffect(() => {
    api.get("/api/health/problems").then(setProblems).catch(() => {});
  }, [logs]);
  const { widths, setWidth, totalWidth } = useColumnWidths({ time: 180, action: 190, resource: 240, actor: 170, detail: 340 });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/api/admin/logs")
      .then(data => { setLogs(data); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // **Server-side since 2026-09-07, and the reason is the sentence this comment
  // used to carry.** It said "this is the 'no export' gap that made clearing the
  // log irreversible" — and it was a client-side dump of `logs`, which holds at
  // most what GET /api/admin/logs returns: 500 rows. So the one button offered
  // as a safety net beside an irreversible Clear silently saved the newest 500
  // entries and dropped the rest, which is worse than no button, because it
  // looks like a backup.
  //
  // The route exports everything the caller may see, and writes `log.export` on
  // the way out — an access to the audit trail that leaves no trace of itself is
  // the one gap a tamper-evident log cannot afford.
  //
  // Through api.saveFile, never a link: apiNavigation.test.js refuses
  // <a href="/api/…"> and window.open because a navigation cannot carry the
  // bearer token, which once shipped five broken buttons including the database
  // backup. The catch is not optional either — downloadErrors.test.js refuses a
  // saveFile with nothing handling the rejection.
  const handleExport = () => {
    setError(null);
    api.saveFile("/api/admin/logs/export.tsv").catch(err => setError(err.message));
  };

  return (
    <PortalLayout>
      {/* data-page-actions was here without its data-page-header, so the
          actions wrapped on a phone but the row they sit in never stacked. */}
      <div data-page-header style={{ display: "flex", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Audit Log</h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#6b7280" }}>
            {user?.is_admin ? "All activity" : "Activity in your group"}
          </p>
        </div>
        {user?.is_admin && logs.length > 0 && (
          <div data-page-actions style={{ marginLeft: "auto", display: "flex",
                                          alignItems: "center", gap: 12 }}>
            {/* A Clear history stood beside the download until 2026-09-15. It
                could not work — the log is kept for the life of the project
                and the database refuses the delete — so what stands there now
                is the mechanism that does remove old entries, named exactly as
                the setting is labelled. The download is still worth offering
                here: an admin who came to prune the log wants a copy. */}
            <span style={{ fontSize: 12, color: "#6b7280", maxWidth: 460, textAlign: "right" }}>
              Entries are kept for the life of the project they belong to. Old ones go by Management → Portal settings → "Delete old log entries after (days)".
            </span>
            <Btn variant="secondary" onClick={handleExport}>Download log (TSV)</Btn>
          </div>
        )}
      </div>

      {problems && problems.count > 0 && (
        <div role="alert" style={{ marginBottom: 18, padding: "12px 16px", borderRadius: 8, background: "rgba(185,28,28,0.08)", border: "1px solid rgba(185,28,28,0.4)", color: "#b91c1c", fontSize: 13, display: "flex", gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 16 }}>⚠</span>
          <div>
            <strong>{problems.count} unresolved problem{problems.count === 1 ? "" : "s"} right now.</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {/* Three different problems arrive here, and calling a bucket
                  that answers perfectly well "unreachable" is what made a real
                  incident unreadable — so each says what it actually is. The
                  fallback covers a kind added by a newer backend. */}
              {problems.problems.map(pr => (
                <li key={`${pr.project_id}-${pr.kind}`} style={{ marginTop: 3 }}>
                  <strong>{pr.project_name}</strong> — {PROBLEM_LABELS[pr.kind] ?? "storage problem"}
                  {pr.error && <> ({pr.error})</>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Saying so explicitly, because "no banner" reads the same as "the check
          never ran". An admin should be able to tell those apart at a glance. */}
      {problems && problems.count === 0 && (
        <div role="status" style={{ marginBottom: 18, padding: "10px 16px", borderRadius: 8, background: "rgba(0,150,64,0.07)", border: "1px solid rgba(0,150,64,0.3)", color: "#00551f", fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <span aria-hidden="true">✓</span>
          <span>No unresolved problems. Highlighted rows below are past warnings that have since been resolved.</span>
        </div>
      )}

      {error && (
        <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}><Spinner /> Loading…</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", color: "#6b7280" }}>No log entries yet.</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: totalWidth }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <Th label="Time" sortKey="time" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.time} onWidth={w => setWidth("time", w)} />
                  <Th label="Action" sortKey="action" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.action} onWidth={w => setWidth("action", w)} />
                  <Th label="Resource" sortKey="resource" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.resource} onWidth={w => setWidth("resource", w)} />
                  <Th label="Actor" sortKey="actor" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.actor} onWidth={w => setWidth("actor", w)} />
                  <Th label="Detail" sortKey="detail" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.detail} onWidth={w => setWidth("detail", w)} />
                </tr>
              </thead>
              <tbody>
                {sorted.map(log => (
                  // A warning is something someone has to act on, so it has to
                  // survive a quick scan of a long table — colour alone would
                  // not, hence the marker and the bold detail too.
                  <tr key={log.id} style={{
                    borderBottom: "1px solid #f3f4f6",
                    background: log.level === "warning" ? "rgba(217,119,6,0.08)" : undefined,
                    boxShadow: log.level === "warning" ? "inset 3px 0 0 #b45309" : undefined,
                  }}>
                    <td data-label="Time" style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatWhen(log.created_at, { zone })}
                    </td>
                    <td data-label="Action" style={{ padding: "9px 14px" }}>
                      <ActionBadge action={log.action} />
                    </td>
                    <td data-label="Resource" style={{ padding: "9px 14px", fontSize: 13, fontWeight: 500, color: "#1a1a1a", ...cellClip }}
                        title={log.resource || undefined}>
                      {log.resource}
                    </td>
                    <td data-label="Actor" style={{ padding: "9px 14px", fontSize: 12, color: "#6b7280", fontFamily: "'JetBrains Mono', monospace", ...cellClip }}
                        title={log.actor || undefined}>
                      {log.actor || "—"}
                    </td>
                    {/* A job.fail detail runs to a couple of hundred characters
                        — the whole message, plus the S3 prefix. It is cut to fit
                        the column and read in full from the tooltip. */}
                    <td data-label="Detail" style={{ padding: "9px 14px", fontSize: 12, color: log.level === "warning" ? "#92400e" : "#6b7280", fontWeight: log.level === "warning" ? 600 : 400, ...cellClip }}
                        title={log.detail || undefined}>
                      {log.level === "warning" && <span aria-hidden="true" style={{ marginRight: 5 }}>⚠</span>}
                      {log.detail || <span style={{ color: "#6b7280" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "10px 16px", fontSize: 12, color: "#6b7280", borderTop: "1px solid #f3f4f6" }}>
            {logs.length} entr{logs.length !== 1 ? "ies" : "y"}
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
